import {
  CaretRightFilled,
  LeftOutlined,
  PauseOutlined,
  RightOutlined,
} from "@ant-design/icons";
import { useEffect, useId, useState } from "react";

const speeds = [0.5, 1, 2, 4] as const;

interface TimelineProps {
  currentHour: number;
  isPlaying: boolean;
  onPlayingChange: (playing: boolean) => void;
  onTimeChange: (hour: number) => void;
  reducedMotion: boolean;
  startTime?: string;
}

function clampHour(hour: number) {
  return Math.max(0, Math.min(72, hour));
}

function formatTimelineTime(startTime: string, offset: number) {
  const date = new Date(new Date(startTime).getTime() + offset * 3_600_000);
  return new Intl.DateTimeFormat("zh-CN", {
    timeZone: "Asia/Shanghai",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).format(date);
}

export function Timeline({
  currentHour,
  isPlaying,
  onPlayingChange,
  onTimeChange,
  reducedMotion,
  startTime = "2026-08-24T06:00:00Z",
}: TimelineProps) {
  const [speed, setSpeed] = useState<(typeof speeds)[number]>(1);
  const [speedOpen, setSpeedOpen] = useState(false);
  const speedMenuId = useId();

  useEffect(() => {
    if (!isPlaying) return;
    let tick = 0;
    const frameRate = 30;
    const interval = window.setInterval(() => {
      tick += 1;
      const nextHour = clampHour(
        reducedMotion ? currentHour + tick * 6 : currentHour + (tick * speed) / frameRate,
      );
      onTimeChange(nextHour);
      if (nextHour >= 72) onPlayingChange(false);
    }, reducedMotion ? 1200 : 1000 / frameRate);
    return () => window.clearInterval(interval);
  }, [currentHour, isPlaying, onPlayingChange, onTimeChange, reducedMotion, speed]);

  const previousPoint = Math.max(0, Math.floor((currentHour - 0.001) / 6) * 6);
  const nextPoint = Math.min(72, Math.ceil((currentHour + 0.001) / 6) * 6);

  return (
    <div
      aria-label="台风预测时间轴"
      className="timeline glass-panel"
      data-motion={reducedMotion ? "keyframes" : "continuous"}
      role="region"
    >
      <div className="timeline__controls">
        <button
          aria-label={isPlaying ? "暂停预测路径" : "播放预测路径"}
          className="icon-button icon-button--primary"
          onClick={() => onPlayingChange(!isPlaying)}
          type="button"
        >
          {isPlaying ? <PauseOutlined aria-hidden /> : <CaretRightFilled aria-hidden />}
        </button>
        <button aria-label="上一个预报点" className="icon-button" onClick={() => onTimeChange(previousPoint)} type="button">
          <LeftOutlined aria-hidden />
        </button>
        <button aria-label="下一个预报点" className="icon-button" onClick={() => onTimeChange(nextPoint)} type="button">
          <RightOutlined aria-hidden />
        </button>
        <div className="timeline__speed">
          <button
            aria-controls={speedMenuId}
            aria-expanded={speedOpen}
            aria-label={`播放速度 ${speed} 倍`}
            className="speed-button"
            onClick={() => setSpeedOpen((open) => !open)}
            type="button"
          >
            {speed}×
          </button>
          {speedOpen ? (
            <div className="speed-menu glass-panel" id={speedMenuId} role="menu">
              {speeds.map((option) => (
                <button
                  aria-checked={option === speed}
                  key={option}
                  onClick={() => { setSpeed(option); setSpeedOpen(false); }}
                  role="menuitemradio"
                  type="button"
                >
                  {option}×
                </button>
              ))}
            </div>
          ) : null}
        </div>
      </div>
      <div className="timeline__track">
        <div className="timeline__segments" aria-hidden>
          <span>实况</span><span>预测（72小时）</span><span>远期</span>
        </div>
        <input
          aria-label="未来 72 小时时间"
          max={72}
          min={0}
          onChange={(event) => onTimeChange(Number(event.target.value))}
          step={reducedMotion ? 6 : 0.25}
          style={{ "--timeline-progress": `${(currentHour / 72) * 100}%` } as React.CSSProperties}
          type="range"
          value={currentHour}
        />
        <div className="timeline__ticks" aria-hidden>
          {[0, 12, 24, 36, 48, 60, 72].map((hour) => <span key={hour}>{formatTimelineTime(startTime, hour)}</span>)}
        </div>
      </div>
      <output className="timeline__current">当前时间 {formatTimelineTime(startTime, currentHour)}</output>
      {reducedMotion ? <span className="timeline__reduced-note">减少动态：按预报关键帧步进</span> : null}
    </div>
  );
}
